import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';

import { useWorkspaces } from '@/entities/workspace';
import { originCategory, originKind, originWorkspaceId, readOrigin } from '@/shared/lib/origin';
import { Icons } from '@/shared/ui';

import styles from './BackLink.module.css';

/** Where a resource page leads back to when it was not opened from a catalog page. */
export interface BackTarget {
  to: string;
  label: string;
  /** Spoken name; defaults to "Back to <label>". */
  ariaLabel?: string;
}

const CATEGORY_LABEL = {
  boards: 'nav.myBoards',
  'team-boards': 'nav.teamBoards',
  workspaces: 'nav.workspaces',
  databases: 'nav.myDatabases',
  favorites: 'nav.favorites',
} as const;

function Pill({ target }: { target: BackTarget }) {
  const { t } = useTranslation();
  return (
    <Link
      to={target.to}
      className={styles.back}
      title={target.label}
      aria-label={target.ariaLabel ?? t('resourceNav.backTo', { name: target.label })}
      data-testid="resource-back"
    >
      <Icons.chevronLeft />
      <span className={styles.text}>{target.label}</span>
    </Link>
  );
}

/** A workspace origin is honoured only while the viewer still sees that workspace. */
function WorkspaceOrigin({ origin, fallback }: { origin: string; fallback: BackTarget | null }) {
  const { t } = useTranslation();
  const workspaces = useWorkspaces();
  if (workspaces.isPending) return null;
  const id = originWorkspaceId(origin);
  const workspace = workspaces.data?.find((item) => item.id === id);
  if (!workspace) return fallback && <Pill target={fallback} />;
  const label =
    workspace.workspace_type === 'personal' ? t('home.personalWorkspace') : workspace.name;
  return <Pill target={{ to: origin, label }} />;
}

/**
 * The back link of a board or database page. It leads to the catalog page the resource was opened
 * from (Home, a workspace with its section and filters, a category list) when the router state
 * names one that is still valid; a direct link, a reload in a new tab or a stale origin use the
 * page's own `fallback` (null = nothing to offer).
 */
export function BackLink({ fallback }: { fallback: BackTarget | null }) {
  const { t } = useTranslation();
  const location = useLocation();
  const origin = readOrigin(location);
  const kind = originKind(origin);
  if (origin && kind === 'home') return <Pill target={{ to: origin, label: t('nav.home') }} />;
  if (origin && kind === 'workspace')
    return <WorkspaceOrigin origin={origin} fallback={fallback} />;
  if (origin && kind === 'category') {
    const category = originCategory(origin) as keyof typeof CATEGORY_LABEL;
    return <Pill target={{ to: origin, label: t(CATEGORY_LABEL[category]) }} />;
  }
  return fallback && <Pill target={fallback} />;
}
